import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus,
  Instagram,
  Loader2,
  Users,
  MoreVertical,
  Trash2,
  ExternalLink,
  Mail,
  MessageCircle,
  Video,
  Search,
} from 'lucide-react';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CreatorOutreach = () => {
  const { activeBrand } = useAuth();
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newHandle, setNewHandle] = useState('');
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchCreators();
  }, [activeBrand]);

  const fetchCreators = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/creator-outreach?brand=${activeBrand}`);
      setCreators(response.data);
    } catch (error) {
      console.error('Error fetching creators:', error);
      toast.error('Failed to load creators');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCreator = async () => {
    if (!newHandle.trim()) {
      toast.error('Please enter an Instagram handle');
      return;
    }

    try {
      setAdding(true);
      const response = await api.post(`/creator-outreach?brand=${activeBrand}`, {
        instagram_handle: newHandle.trim()
      });
      setCreators([response.data, ...creators]);
      setShowAddDialog(false);
      setNewHandle('');
      toast.success('Creator added successfully!');
      // Navigate to the new creator pack
      navigate(`/creator-outreach/${response.data.id}`);
    } catch (error) {
      console.error('Error adding creator:', error);
      toast.error(error.response?.data?.detail || 'Failed to add creator');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteCreator = async (creatorId) => {
    try {
      setDeletingId(creatorId);
      await api.delete(`/creator-outreach/${creatorId}`);
      setCreators(creators.filter(c => c.id !== creatorId));
      toast.success('Creator removed');
    } catch (error) {
      console.error('Error deleting creator:', error);
      toast.error('Failed to delete creator');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-zinc-100 text-zinc-700';
      case 'pitched': return 'bg-blue-100 text-blue-700';
      case 'responded': return 'bg-amber-100 text-amber-700';
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'declined': return 'bg-red-100 text-red-700';
      default: return 'bg-zinc-100 text-zinc-700';
    }
  };

  const filteredCreators = creators.filter(creator =>
    creator.instagram_handle.toLowerCase().includes(searchTerm.toLowerCase()) ||
    creator.instagram_data?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="creator-outreach-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light text-zinc-900 tracking-tight">Creator Outreach</h1>
          <p className="text-zinc-500 mt-1">Build relationships with content creators</p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-[#D4AF37] hover:bg-amber-600 text-white"
          data-testid="add-creator-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Creator
        </Button>
      </div>

      {/* Search */}
      {creators.length > 0 && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search creators..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-creators-input"
          />
        </div>
      )}

      {/* Creator List */}
      {filteredCreators.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-2xl flex items-center justify-center mb-4">
              <Instagram className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-medium text-zinc-900 mb-2">No creators yet</h3>
            <p className="text-zinc-500 text-center max-w-sm mb-4">
              Start building your creator network by adding Instagram handles of potential collaborators
            </p>
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-[#D4AF37] hover:bg-amber-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Creator
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCreators.map((creator) => (
            <Card
              key={creator.id}
              className="group hover:shadow-lg transition-all cursor-pointer border-zinc-200 hover:border-[#D4AF37]"
              onClick={() => navigate(`/creator-outreach/${creator.id}`)}
              data-testid={`creator-card-${creator.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Profile Picture */}
                  <div className="relative">
                    <img
                      src={creator.instagram_data?.profile_pic_url || `https://unavatar.io/instagram/${creator.instagram_handle}`}
                      alt={creator.instagram_handle}
                      className="w-16 h-16 rounded-full object-cover border-2 border-zinc-100"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${creator.instagram_handle}&background=D4AF37&color=fff`;
                      }}
                    />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-full flex items-center justify-center">
                      <Instagram className="w-3 h-3 text-white" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-zinc-900 truncate">
                          @{creator.instagram_handle}
                        </h3>
                        {creator.instagram_data?.full_name && (
                          <p className="text-sm text-zinc-500 truncate">
                            {creator.instagram_data.full_name}
                          </p>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://instagram.com/${creator.instagram_handle}`, '_blank');
                            }}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View on Instagram
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCreator(creator.id);
                            }}
                            disabled={deletingId === creator.id}
                          >
                            {deletingId === creator.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Status & Quick Info */}
                    <div className="flex items-center gap-2 mt-3">
                      <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(creator.status)}`}>
                        {creator.status || 'draft'}
                      </span>
                      {creator.pitch?.video && (
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
                          <Video className="w-3 h-3" />
                          Video
                        </span>
                      )}
                      {creator.pitch?.email_copy && (
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                        </span>
                      )}
                      {creator.pitch?.dm_copy && (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Creator Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md !bg-zinc-900 !border-zinc-800 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/30">
                <Instagram className="w-6 h-6 text-white" />
              </div>
              <span className="text-white font-medium">Add Creator</span>
            </DialogTitle>
            <DialogDescription className="!text-zinc-400 mt-3">
              Enter the Instagram handle or profile URL of the creator you want to collaborate with.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-4">
            <div>
              <label className="text-sm font-medium text-zinc-300 mb-2 block">
                Instagram Handle or URL
              </label>
              <Input
                placeholder="@username or instagram.com/username"
                value={newHandle}
                onChange={(e) => setNewHandle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCreator()}
                className="!bg-zinc-800 !border-zinc-700 !text-white placeholder:!text-zinc-500 focus:!border-[#D4AF37] focus:!ring-[#D4AF37]/30 h-11"
                data-testid="instagram-handle-input"
              />
              <p className="text-xs text-zinc-500 mt-2 flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-zinc-500" />
                Examples: @johndoe, johndoe, or https://instagram.com/johndoe
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                variant="ghost" 
                onClick={() => setShowAddDialog(false)}
                className="text-zinc-400 hover:text-white hover:!bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddCreator}
                disabled={adding || !newHandle.trim()}
                className="bg-gradient-to-r from-[#D4AF37] to-amber-500 hover:from-amber-500 hover:to-[#D4AF37] text-white shadow-lg shadow-amber-500/25 px-6"
                data-testid="confirm-add-creator-btn"
              >
                {adding ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Creator
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreatorOutreach;
